import { forwardRef, useCallback, useEffect, useRef } from 'react'

import { tv, type VariantProps } from 'tailwind-variants'

import { useReducedMotion } from '../../../hooks/use-reduced-motion'
import { cn } from '../../../utils/cn'

const shimmerStyle = {
  backgroundImage:
    'linear-gradient(90deg, var(--color-surface-200) 25%, var(--color-surface) 50%, var(--color-surface-200) 75%)',
  backgroundSize: '200% 100%',
  backgroundPosition: '-200% 0'
} satisfies React.CSSProperties

const shimmerKeyframes = { backgroundPosition: ['-200% 0', '200% 0'] }
const shimmerOptions = {
  duration: 1500,
  easing: 'ease-in-out',
  iterations: Infinity
} satisfies KeyframeAnimationOptions

const skeletonVariants = tv({
  base: 'bg-surface-200',
  variants: {
    variant: {
      pill: 'rounded-full',
      square: 'rounded-sm',
      circle: 'aspect-square rounded-full'
    }
  },
  defaultVariants: {
    variant: 'pill'
  }
})

export type SkeletonProps = React.ComponentPropsWithRef<'div'> &
  VariantProps<typeof skeletonVariants>

export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(
  ({ variant, className, style, ...props }, ref) => {
    const elementRef = useRef<HTMLDivElement | null>(null)
    const reducedMotion = useReducedMotion()

    const attachRef = useCallback(
      (element: HTMLDivElement | null) => {
        elementRef.current = element
        if (typeof ref === 'function') ref(element)
        else if (ref) ref.current = element
      },
      [ref]
    )

    useEffect(() => {
      const element = elementRef.current
      if (reducedMotion || !element?.animate) return

      const animation = element.animate(shimmerKeyframes, shimmerOptions)
      // React detaches the node before passive cleanup; committing styles then throws.
      return () => animation.cancel()
    }, [reducedMotion])

    return (
      <div
        ref={attachRef}
        data-slot='skeleton'
        className={cn(skeletonVariants({ variant }), className)}
        style={{ ...shimmerStyle, ...style }}
        {...props}
      />
    )
  }
)
Skeleton.displayName = 'Skeleton'
